import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CalendlyWidget, { CalendlyLink, CalendlyScheduleButton, CalendlyMeetingCard } from '../components/CalendlyWidget';
import { Calendar, Code, Settings, ExternalLink, CheckCircle } from 'lucide-react';

const CalendlyIntegration = () => {
  const [calendlyUrl, setCalendlyUrl] = useState('https://calendly.com/your-username/30min');
  const [widgetType, setWidgetType] = useState('inline');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Calendly Integration</h1>
          </div>
          <p className="text-lg text-gray-600">
            Connect your Calendly account to enable seamless meeting scheduling
          </p>
        </div>

        {/* Quick Setup */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Quick Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Calendly URL
              </label>
              <Input
                type="url"
                value={calendlyUrl}
                onChange={(e) => setCalendlyUrl(e.target.value)}
                placeholder="https://calendly.com/your-username/30min"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find your Calendly URL at: <a href="https://calendly.com/event_types" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">calendly.com/event_types</a>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Widget Type
              </label>
              <div className="flex gap-3">
                {['inline', 'popup', 'link'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setWidgetType(type)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      widgetType === type
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Preview */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Live Preview
            </h2>

            {widgetType === 'inline' && (
              <CalendlyWidget 
                calendlyUrl={calendlyUrl}
                type="inline"
              />
            )}

            {widgetType === 'popup' && (
              <Card>
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-xl font-semibold">Popup Widget Demo</h3>
                  <p className="text-gray-600">Click the button below to open Calendly in a popup</p>
                  <CalendlyWidget 
                    calendlyUrl={calendlyUrl}
                    type="popup"
                    text="Schedule a Meeting"
                  />
                </CardContent>
              </Card>
            )}

            {widgetType === 'link' && (
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-8 space-y-4">
                    <h3 className="text-xl font-semibold mb-4">Link Examples</h3>
                    
                    <div className="space-y-3">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Simple Link:</p>
                        <CalendlyLink calendlyUrl={calendlyUrl} />
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-3">Button Variants:</p>
                        <div className="space-y-2">
                          <CalendlyScheduleButton 
                            calendlyUrl={calendlyUrl}
                            variant="primary"
                          />
                          <CalendlyScheduleButton 
                            calendlyUrl={calendlyUrl}
                            variant="secondary"
                            text="Book Appointment"
                          />
                          <CalendlyScheduleButton 
                            calendlyUrl={calendlyUrl}
                            variant="outline"
                            text="Schedule Call"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <CalendlyMeetingCard
                  calendlyUrl={calendlyUrl}
                  title="30-Minute Consultation"
                  description="Let's discuss your needs and how we can help"
                  duration="30 min"
                  features={[
                    "Free consultation",
                    "Personalized recommendations",
                    "Q&A session",
                    "Next steps planning"
                  ]}
                />
              </div>
            )}
          </div>

          {/* Code Examples */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Code className="w-6 h-6" />
              Code Examples
            </h2>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How to Use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-sm">
{widgetType === 'inline' && `// Inline Widget (Full page embed)
import CalendlyWidget from '../components/CalendlyWidget';

<CalendlyWidget 
  calendlyUrl="${calendlyUrl}"
  type="inline"
/>`}

{widgetType === 'popup' && `// Popup Button
import CalendlyWidget from '../components/CalendlyWidget';

<CalendlyWidget 
  calendlyUrl="${calendlyUrl}"
  type="popup"
  text="Schedule a Meeting"
/>`}

{widgetType === 'link' && `// Simple Link
import { CalendlyLink } from '../components/CalendlyWidget';

<CalendlyLink 
  calendlyUrl="${calendlyUrl}"
  text="Schedule with Calendly"
/>

// Button Style
import { CalendlyScheduleButton } from '../components/CalendlyWidget';

<CalendlyScheduleButton 
  calendlyUrl="${calendlyUrl}"
  variant="primary" // or "secondary" or "outline"
/>

// Meeting Card
import { CalendlyMeetingCard } from '../components/CalendlyWidget';

<CalendlyMeetingCard
  calendlyUrl="${calendlyUrl}"
  title="30-Minute Consultation"
  duration="30 min"
  features={["Feature 1", "Feature 2"]}
/>`}
                  </pre>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Integration Steps
                  </h4>
                  <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li>Sign up for Calendly at <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" className="underline">calendly.com</a></li>
                    <li>Create your meeting types (e.g., 30min, 1hour)</li>
                    <li>Copy your Calendly URL from the event type</li>
                    <li>Paste it in the input above</li>
                    <li>Choose your preferred widget type</li>
                    <li>Copy the code and use it in your pages!</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-lg">Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Automatic timezone detection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Email notifications and reminders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Calendar sync (Google, Outlook, iCal)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Customizable booking pages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Multiple meeting types</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Mobile-friendly interface</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Help */}
            <Card className="mt-4 bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Need Help?
                </h4>
                <div className="space-y-2 text-sm">
                  <a 
                    href="https://help.calendly.com/hc/en-us" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    → Calendly Help Center
                  </a>
                  <a 
                    href="https://developer.calendly.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    → Calendly API Documentation
                  </a>
                  <a 
                    href="https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    → Embed Options Guide
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendlyIntegration;
